// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { resultCountProps } from "@store/Themes/common/props/resultCount";
import { tableProps } from "@store/Themes/common/props/table";
import { typographyGetNoResultsAltProps } from "@store/Themes/common/props/typography";
import constants from "@store/constants";
import { useFailedToLoadLedgers, useLedgersDataView } from "@store/Framework/Store/Store/Data/Ledgers/LedgersHooks";
import { useLedgersParameter } from "@store/Framework/Store/Store/Pages/BillingStatus/BillingStatusHooks";
import updateSearchFields from "@store/Framework/Store/Store/Pages/BillingStatus/Handlers/UpdateSearchFields";
import { LedgerModel } from "@store/Framework/Types/ApiModels";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { useCurrentLanguage } from "@toolkit/Framework/Store/Context/ContextHooks";
import cloneDeep from "lodash/cloneDeep";
import * as React from "react";
import { getProp } from "@insite/mobius/utilities";
import { css } from "styled-components";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import Button from "@insite/mobius/Button";
import getColor from "@insite/mobius/utilities/getColor";

const Pagination = getWidgetComponent("BillingStatus/BillingStatusListPagination");

type SortOrderOptions = "ascending" | "descending" | false;

const enum fields {
    enablePayments = "enablePayments",
    showInvoiceDate = "showInvoiceDate",
    showGrossAmount = "showGrossAmount",
    showOpenAmount = "showOpenAmount",
    showInvoiceNumber = "showInvoiceNumber",
    showDocumentType = "showDocumentType",
    showPoNumber = "showPoNumber",
    showDueDate = "showDueDate",
    showStatus = "showStatus",
}

interface BillingStatusListTableProps extends WidgetProps {
    fields: {
        [fields.enablePayments]: boolean;
        [fields.showInvoiceDate]: boolean;
        [fields.showGrossAmount]: boolean;
        [fields.showOpenAmount]: boolean;
        [fields.showInvoiceNumber]: boolean;
        [fields.showDocumentType]: boolean;
        [fields.showPoNumber]: boolean;
        [fields.showDueDate]: boolean;
        [fields.showStatus]: boolean;
    };
}

export interface BillingStatusListTableStyles {
    container?: InjectableCss;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
    dataTable?: DataTableProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    selectInvoiceHeader?: DataTableCellProps;
    invoiceDateHeader?: DataTableCellProps;
    grossAmountHeader?: DataTableCellProps;
    openAmountHeader?: DataTableCellProps;
    invoiceHeader?: DataTableCellProps;
    documentTypeHeader?: DataTableCellProps;
    poNumberHeader?: DataTableCellProps;
    dueDateHeader?: DataTableCellProps;
    statusHeader?: DataTableCellProps;
    invoiceDateCell?: DataTableCellProps;
    grossAmountCell?: DataTableCellProps;
    openAmountCell?: DataTableCellProps;
    invoiceCell?: DataTableCellProps;
    documentTypeCell?: DataTableCellProps;
    poNumberCell?: DataTableCellProps;
    dueDateCell?: DataTableCellProps;
    statusCell?: DataTableCellProps;
    loadingOverlay?: LoadingOverlayProps;
    tableWrapper?: InjectableCss;
    countWrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    selectedInvoicesWrapper?: InjectableCss;
    selectedInvoicesTitle?: TypographyPresentationProps;
    selectedInvoicesTotalWrapper?: InjectableCss;
    selectedInvoicesTotalLabel?: TypographyPresentationProps;
    selectedInvoicesTotalValue?: TypographyPresentationProps;
}

export const tableStyles: BillingStatusListTableStyles = {
    loadingOverlay: tableProps.loadingOverlay,
    countWrapper: resultCountProps.wrapper,
    countText: resultCountProps.countText,
    tableWrapper: tableProps.wrapper,
    noResultsText: typographyGetNoResultsAltProps({ spaced: true }),
    dataTable: tableProps.dataTable,
    selectedInvoicesWrapper: {
        css: css`
            box-shadow: ${getProp("theme.shadows.dark20")};
            padding: ${pxToRem(20)};
            margin-bottom: ${pxToRem(20)};
        `,
    },
    selectedInvoicesTitle: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
            margin-bottom: ${pxToRem(10)};
        `,
    },
    selectedInvoicesTotalWrapper: {
        css: css`
            display: table;
            width: 100%;
            margin: ${pxToRem(20, 0)};
            padding: ${pxToRem(10, 0)};
            border-top: 1px solid ${getColor("common.border")};
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    selectedInvoicesTotalLabel: {
        css: css`
            display: table-cell;
        `,
    },
    selectedInvoicesTotalValue: {
        css: css`
            display: table-cell;
            text-align: right;
            padding-left: ${pxToRem(10)};
            white-space: nowrap;
        `,
    },
};

const styles = tableStyles;

const BillingStatusListTable = ({
    fields: {
        enablePayments,
        showInvoiceDate,
        showGrossAmount,
        showOpenAmount,
        showInvoiceNumber,
        showDocumentType,
        showPoNumber,
        showDueDate,
        showStatus,
    },
}: BillingStatusListTableProps) => {
    const language = useCurrentLanguage();
    const dispatch = useAppDispatch();
    const ledgersParameter = useLedgersParameter();
    const baseLedgersDataView = useLedgersDataView(ledgersParameter);
    const ledgersDataView = usePersistentDataView(baseLedgersDataView);
    const failedToLoadLedgers = useFailedToLoadLedgers();
    const ledgersCount = (ledgersDataView as any)?.pagination?.totalItemCount ?? 0;
    const ledgersCountLabel = ledgersCount === 1 ? translate("Billing Transaction") : translate("Billing Transactions");
    const [rows, setRows] = React.useState<LedgerModel[]>();
    const [selectedInvoices, setSelectedInvoices] = React.useState<LedgerModel[]>();
    const [selectedInvoicesTotal, setSelectedInvoicesTotal] = React.useState<number>(0);
    const [hasInvoicesToPay, setHasInvoicesToPay] = React.useState(false);

    React.useEffect(() => {
        if (ledgersDataView.value) {
            const ledgerRows = ledgersDataView.value.map(ledger => {
                return {
                    id: ledger.id,
                    invoiceDate: ledger.invoiceDate
                        ? getLocalizedDateTime({
                              dateTime: new Date(ledger.invoiceDate),
                              language,
                          })
                        : "",
                    grossAmountDisplay: ledger.grossAmountDisplay || "",
                    grossAmount: ledger.grossAmount || 0,
                    openAmountDisplay: ledger.openAmountDisplay || "",
                    openAmount: ledger.openAmount || 0,
                    isPayable: ledger.isPayable || false,
                    dueDate: ledger.dueDate
                        ? getLocalizedDateTime({
                              dateTime: new Date(ledger.dueDate),
                              language,
                          })
                        : "",
                    documentType: ledger.documentType || "",
                    status: ledger.status || "",
                    poNumber: ledger.poNumber || "",
                    invoiceNumber: ledger.invoiceNumber || "",
                    isSelected: ledger.isSelected || false,
                };
            });
            setRows(ledgerRows);
        }
    }, [ledgersDataView.value]);

    React.useEffect(() => {
        if (selectedInvoices) {
            let invoicesTotal = 0;
            selectedInvoices.forEach(invoice => {
                invoicesTotal += invoice.openAmount || 0;
            });
            setSelectedInvoicesTotal(invoicesTotal);
        }
    }, [selectedInvoices]);

    const headerClick = (sortField: string) => {
        let sortOrder: "asc" | "desc" = "asc";

        if (ledgersParameter.sortBy === sortField) {
            sortOrder = ledgersParameter.sortOrder === "asc" ? "desc" : "asc";
        }

        dispatch(
            updateSearchFields({
                sortBy: sortField,
                sortOrder,
            }),
        );
    };

    const sorted = (sortField: string): SortOrderOptions => {
        if (ledgersParameter.sortBy !== sortField) {
            return false;
        }

        return ledgersParameter.sortOrder === "desc" ? "descending" : "ascending";
    };

    if (failedToLoadLedgers) {
        return null;
    }

    // Only open invoices with a remaining balance are payable, credit memos and charge backs can never be selected
    const hasPayableInvoices = !!rows?.some(row => row.isPayable);
    const isAllSelected = hasPayableInvoices && !!rows?.every(row => !row.isPayable || row.isSelected);

    const selectAllChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        if (rows) {
            const currentRows = cloneDeep(rows);
            currentRows.forEach(row => {
                row.isSelected = value && !!row.isPayable;
            });

            setCurrentRows(currentRows);
        }
    };

    const selectBillingStatusChangeHandler = (value: boolean, index: number) => {
        if (rows && rows[index].isPayable) {
            const currentRows = cloneDeep(rows);
            currentRows[index].isSelected = value;

            setCurrentRows(currentRows);
        }
    };

    const setCurrentRows = (currentRows: LedgerModel[]) => {
        setRows(currentRows);

        const selectedInvoicesCollection = currentRows.filter(invoice => invoice.isSelected);
        setSelectedInvoices(selectedInvoicesCollection);

        setHasInvoicesToPay(selectedInvoicesCollection.length > 0);
    };

    return (
        <DataViewLoadingOverlay dataView={ledgersDataView} {...styles.loadingOverlay}>
            {!ledgersDataView.value?.length ? (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography {...styles.noResultsText}>{translate("No billing transactions found")}</Typography>
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.container}>
                    {enablePayments !== false && (
                        <StyledWrapper {...styles.selectedInvoicesWrapper}>
                            <Typography {...styles.selectedInvoicesTitle}>{translate("Selected Invoices")}</Typography>
                            <StyledWrapper {...styles.selectedInvoicesTotalWrapper}>
                                <Typography {...styles.selectedInvoicesTotalLabel}>{translate("Total")}</Typography>
                                <Typography
                                    {...styles.selectedInvoicesTotalValue}
                                >{`$${selectedInvoicesTotal.toFixed(2)}`}</Typography>
                            </StyledWrapper>
                            <Button disabled={!hasInvoicesToPay}>{translate("Pay Selected Invoices")}</Button>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.countWrapper}>
                        <Typography {...styles.countText}>
                            {ledgersCount} {ledgersCountLabel}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.tableWrapper}>
                        <DataTable {...styles.dataTable}>
                            <DataTableHead>
                                {enablePayments !== false && (
                                    <DataTableHeader {...styles.selectInvoiceHeader}>
                                        <Checkbox
                                            checked={isAllSelected}
                                            disabled={!hasPayableInvoices}
                                            onChange={selectAllChangeHandler}
                                            data-test-selector="billingStatus_selectAllItems"
                                        />
                                    </DataTableHeader>
                                )}
                                {showInvoiceDate !== false && (
                                    <DataTableHeader
                                        sorted={sorted("invoiceDate")}
                                        onSortClick={() => headerClick("invoiceDate")}
                                        {...styles.invoiceDateHeader}
                                    >
                                        {translate("Invoice Date")}
                                    </DataTableHeader>
                                )}
                                {showGrossAmount !== false && (
                                    <DataTableHeader
                                        sorted={sorted("grossAmount")}
                                        onSortClick={() => headerClick("grossAmount")}
                                        {...styles.grossAmountHeader}
                                    >
                                        {translate("Gross Amount")}
                                    </DataTableHeader>
                                )}
                                {showOpenAmount !== false && (
                                    <DataTableHeader {...styles.openAmountHeader}>
                                        {translate("Open Amount")}
                                    </DataTableHeader>
                                )}
                                {showInvoiceNumber !== false && (
                                    <DataTableHeader
                                        sorted={sorted("invoiceNumber")}
                                        onSortClick={() => headerClick("invoiceNumber")}
                                        {...styles.invoiceHeader}
                                    >
                                        {translate("Invoice")}
                                    </DataTableHeader>
                                )}
                                {showDocumentType !== false && (
                                    <DataTableHeader
                                        sorted={sorted("documentType")}
                                        onSortClick={() => headerClick("documentType")}
                                        {...styles.documentTypeHeader}
                                    >
                                        {translate("Document Type")}
                                    </DataTableHeader>
                                )}
                                {showPoNumber !== false && (
                                    <DataTableHeader
                                        sorted={sorted("poNumber")}
                                        onSortClick={() => headerClick("poNumber")}
                                        {...styles.poNumberHeader}
                                    >
                                        {translate("PO Number")}
                                    </DataTableHeader>
                                )}
                                {showDueDate !== false && (
                                    <DataTableHeader
                                        sorted={sorted("dueDate")}
                                        onSortClick={() => headerClick("dueDate")}
                                        {...styles.dueDateHeader}
                                    >
                                        {translate("Due Date")}
                                    </DataTableHeader>
                                )}
                                {showStatus !== false && (
                                    <DataTableHeader
                                        sorted={sorted("status")}
                                        onSortClick={() => headerClick("status")}
                                        {...styles.statusHeader}
                                    >
                                        {translate("Status")}
                                    </DataTableHeader>
                                )}
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                {rows?.map(
                                    (
                                        {
                                            id,
                                            invoiceDate,
                                            grossAmountDisplay,
                                            dueDate,
                                            documentType,
                                            status,
                                            poNumber,
                                            invoiceNumber,
                                            isSelected,
                                            openAmountDisplay,
                                            isPayable,
                                        },
                                        index,
                                    ) => (
                                        <DataTableRow
                                            data-test-selector="billingStatus_transactionLine"
                                            data-test-key={id}
                                            key={id}
                                            {...styles.dataTableRow}
                                        >
                                            {enablePayments !== false && (
                                                <DataTableCell>
                                                    <Checkbox
                                                        checked={isSelected}
                                                        disabled={!isPayable}
                                                        onChange={(e, value) =>
                                                            selectBillingStatusChangeHandler(value, index)
                                                        }
                                                        data-test-selector="billingStatus_selectItem"
                                                    ></Checkbox>
                                                </DataTableCell>
                                            )}
                                            {showInvoiceDate !== false && (
                                                <DataTableCell
                                                    {...styles.invoiceDateCell}
                                                    data-test-selector="billingStatus_invoiceDate"
                                                >
                                                    {invoiceDate}
                                                </DataTableCell>
                                            )}
                                            {showGrossAmount !== false && (
                                                <DataTableCell
                                                    {...styles.grossAmountCell}
                                                    data-test-selector="billingStatus_grossAmount"
                                                >
                                                    {grossAmountDisplay}
                                                </DataTableCell>
                                            )}
                                            {showOpenAmount !== false && (
                                                <DataTableCell
                                                    {...styles.openAmountCell}
                                                    data-test-selector="billingStatus_openAmount"
                                                >
                                                    {openAmountDisplay || "TBD"}
                                                </DataTableCell>
                                            )}
                                            {showInvoiceNumber !== false && (
                                                <DataTableCell
                                                    {...styles.invoiceCell}
                                                    data-test-selector="billingStatus_invoiceNumber"
                                                >
                                                    {invoiceNumber}
                                                </DataTableCell>
                                            )}
                                            {showDocumentType !== false && (
                                                <DataTableCell
                                                    {...styles.documentTypeCell}
                                                    data-test-selector="billingStatus_documentType"
                                                >
                                                    {siteMessage(`${constants.ledger.documentType}_${documentType}`)}
                                                </DataTableCell>
                                            )}
                                            {showPoNumber !== false && (
                                                <DataTableCell
                                                    {...styles.poNumberCell}
                                                    data-test-selector="billingStatus_poNumber"
                                                >
                                                    {poNumber}
                                                </DataTableCell>
                                            )}
                                            {showDueDate !== false && (
                                                <DataTableCell
                                                    {...styles.dueDateCell}
                                                    data-test-selector="billingStatus_dueDate"
                                                >
                                                    {dueDate}
                                                </DataTableCell>
                                            )}
                                            {showStatus !== false && (
                                                <DataTableCell
                                                    {...styles.statusCell}
                                                    data-test-selector="billingStatus_status"
                                                >
                                                    {siteMessage(`${constants.ledger.status}_${status}`)}
                                                </DataTableCell>
                                            )}
                                        </DataTableRow>
                                    ),
                                )}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                    <Pagination />
                </StyledWrapper>
            )}
        </DataViewLoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: BillingStatusListTable,
    definition: {
        group: constants.widgetGroup.billingStatus,
        displayName: "Billing Status List Table",
        allowedContexts: [constants.pageType.billingStatus],
        fieldDefinitions: [
            {
                name: fields.enablePayments,
                displayName: "Enable Payment",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showInvoiceDate,
                displayName: "Invoice Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showGrossAmount,
                displayName: "Gross Amount",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showOpenAmount,
                displayName: "Open Amount",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showInvoiceNumber,
                displayName: "Invoice Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showDocumentType,
                displayName: "Document Type",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showPoNumber,
                displayName: "PO Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showDueDate,
                displayName: "Due Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
        ],
        isSystem: true,
    },
};

export default widgetModule;
